import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/**
 * Stickyboard UI state, persisted per-user in localStorage.
 *
 * The drawer opens from the left-rail icon (single click). Width snaps to
 * three breakpoints; "held" mode pushes the chat aside, "peek" overlays it.
 */

export const STICKYBOARD_DRAWER_WIDTHS = [480, 760, 1080] as const;
export const STICKYBOARD_DRAWER_DEFAULT_WIDTH = 480;

/** Whether the Stickyboard drawer is currently open. */
export const stickyboardDrawerVisibleAtom = atomWithStorage<boolean>(
  'opal-stickyboard-drawer-visible',
  false,
  undefined,
  // getOnInit so a docked-open drawer is known synchronously on first render —
  // otherwise BaseLayout renders content full-width, then shifts once the atom
  // hydrates (a visible layout jump on every reload).
  { getOnInit: true }
);

/** Current drawer width (one of STICKYBOARD_DRAWER_WIDTHS, but free during drag). */
export const stickyboardDrawerWidthAtom = atomWithStorage<number>(
  'opal-stickyboard-drawer-width',
  STICKYBOARD_DRAWER_DEFAULT_WIDTH
);

/** Auto-hide the drawer when the user clicks away (iOS-style pill toggle). */
export const stickyboardAutoHideAtom = atomWithStorage<boolean>('opal-stickyboard-auto-hide', true);

/** Opt out of the urgent-zero confetti celebration. */
export const stickyboardConfettiOptOutAtom = atomWithStorage<boolean>(
  'opal-stickyboard-confetti-opt-out',
  false
);

/**
 * The last time this client fired an auto-curate pass (epoch ms). Used to
 * avoid re-firing the Steward on every navigation — only fire when the board
 * has changed since this marker. Null = never fired.
 */
export const stickyboardLastCuratedAtom = atomWithStorage<number | null>(
  'opal-stickyboard-last-curated',
  null,
  undefined,
  // getOnInit so the marker is read from storage synchronously on first render —
  // otherwise the auto-curate gate sees `null` before hydration and fires a
  // (now-suppressed) Steward pass on every fresh board open.
  { getOnInit: true }
);

/**
 * "Ask the Steward" conversation state. Session-only (NOT persisted) — held in
 * an atom rather than component state so the thread survives the drawer's
 * peek/dock toggles and is shared between the drawer and the full-page bar.
 * Only the latest answer is kept (Spotlight-style); the full transcript lives
 * server-side in the agent's memory thread. Reset = clear this back to nulls.
 */
export type StickyboardAskState = {
  memoryId: string | null;
  etag: string | null;
  answer: string;
};

export const stickyboardAskAtom = atom<StickyboardAskState>({
  memoryId: null,
  etag: null,
  answer: '',
});

/**
 * Board filter: which sections / stacks to show. Empty array on a dimension =
 * no filter on that dimension. The empty string `''` is the "Unsorted" /
 * "No stack" bucket (cards whose effective section / stack id is absent).
 * OR within a dimension, AND across dimensions. Persisted per-user (the user
 * chose persistence) with `getOnInit` so the filter is applied synchronously on
 * first render instead of flashing the full board before hydration.
 */
export type StickyboardFilter = {
  sections: string[];
  stacks: string[];
};

export const stickyboardFilterAtom = atomWithStorage<StickyboardFilter>(
  'opal-stickyboard-filter',
  { sections: [], stacks: [] },
  undefined,
  { getOnInit: true }
);
